import React from "react";
import {
  useRecordContext,
  useTranslate,
  useDataProvider,
  useRefresh,
  useGetList,
} from "ra-core";
import { useState } from "react";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { DeleteButton } from "@/components/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User,
  Flame,
  Info,
  CheckSquare,
  Mail,
  Phone,
  Linkedin,
  Building2,
  ExternalLink,
  Plus,
  X,
  Check,
} from "lucide-react";
import { Link } from "react-router";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { DetailSidePanelSection } from "../misc/DetailSidePanelSection";
import { InlineEditField } from "../misc/InlineEditField";
import type { Contact, Company } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { contactGender, translateContactGenderLabel } from "./contactModel";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const { data: companies = [] } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  if (!record) return null;

  const companyChoices = companies.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  const genderChoices = contactGender.map((g) => ({
    value: g.value,
    label: translateContactGenderLabel(g, translate),
  }));

  return (
    <div className="space-y-3">
      {/* Status e Tags */}
      <DetailSidePanelSection
        title="Status & Tags"
        icon={<Flame className="w-4 h-4 text-orange-500" />}
        defaultOpen={true}
      >
        <div className="space-y-2.5">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
              {translate("resources.notes.fields.status")}
            </span>
            <ContactStatusSelector />
          </div>

          <div className="pt-2 border-t border-border/40">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
              {translate("resources.tags.name", { smart_count: 2 })}
            </span>
            <TagsListEdit />
          </div>
        </div>
      </DetailSidePanelSection>

      {/* Informações de Contato */}
      <DetailSidePanelSection
        title="Informações de Contato"
        icon={<User className="w-4 h-4 text-primary" />}
        defaultOpen={true}
      >
        <div className="space-y-1">
          {/* Emails */}
          <MultiContactField
            label="E-mail"
            icon={<Mail className="w-3.5 h-3.5 text-muted-foreground" />}
            items={(Array.isArray(record.email_jsonb)
              ? record.email_jsonb
              : []
            ).map((e: any) =>
              typeof e === "object" && e ? e.email || "" : String(e || ""),
            )}
            type="email"
            onUpdate={async (items) => {
              const current = Array.isArray(record.email_jsonb)
                ? record.email_jsonb
                : [];
              const updated = items.map((v, i) => ({
                ...(current[i] || { type: "Work" }),
                email: v,
              }));
              await dataProvider.update("contacts", {
                id: record.id,
                data: { email_jsonb: updated },
                previousData: record,
              });
              refresh();
            }}
            renderItem={(v) => (
              <a
                href={`mailto:${v}`}
                className="font-medium text-primary hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {v}
              </a>
            )}
          />

          {/* Telefones */}
          <MultiContactField
            label="Telefone / Celular"
            icon={<Phone className="w-3.5 h-3.5 text-muted-foreground" />}
            items={(Array.isArray(record.phone_jsonb)
              ? record.phone_jsonb
              : []
            ).map((p: any) =>
              typeof p === "object" && p ? p.number || "" : String(p || ""),
            )}
            type="tel"
            onUpdate={async (items) => {
              const current = Array.isArray(record.phone_jsonb)
                ? record.phone_jsonb
                : [];
              const updated = items.map((v, i) => ({
                ...(current[i] || { type: "Work" }),
                number: v,
              }));
              await dataProvider.update("contacts", {
                id: record.id,
                data: { phone_jsonb: updated },
                previousData: record,
              });
              refresh();
            }}
            renderItem={(v) => (
              <a
                href={`tel:${v}`}
                className="font-medium text-foreground hover:text-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {v}
              </a>
            )}
          />

          <InlineEditField
            label="LinkedIn"
            field="linkedin_url"
            value={record.linkedin_url || ""}
            resource="contacts"
            recordId={record.id}
            previousData={record}
            type="text"
            onSaved={refresh}
            displayValue={
              record.linkedin_url ? (
                <a
                  href={record.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-primary hover:underline inline-flex items-center gap-1"
                >
                  <Linkedin className="w-3.5 h-3.5 text-muted-foreground" />
                  Acessar perfil
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              ) : undefined
            }
          />

          <InlineEditField
            label="Gênero"
            field="gender"
            value={record.gender || ""}
            resource="contacts"
            recordId={record.id}
            previousData={record}
            type="select"
            choices={genderChoices}
            onSaved={refresh}
          />
        </div>
      </DetailSidePanelSection>

      {/* Dados Profissionais e Empresa */}
      <DetailSidePanelSection
        title="Dados Profissionais & Empresa"
        icon={<Building2 className="w-4 h-4 text-blue-500" />}
        defaultOpen={true}
      >
        <div className="space-y-1">
          <InlineEditField
            label="Cargo / Função"
            field="title"
            value={record.title || ""}
            resource="contacts"
            recordId={record.id}
            previousData={record}
            onSaved={refresh}
          />

          <InlineEditField
            label="Empresa / Organização"
            field="company_id"
            value={record.company_id ? String(record.company_id) : ""}
            resource="contacts"
            recordId={record.id}
            previousData={record}
            type="select"
            choices={companyChoices}
            onSaved={refresh}
            displayValue={
              record.company_id ? (
                <Link
                  to={`/companies/${record.company_id}/show`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-primary hover:underline inline-flex items-center gap-1.5"
                >
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  {companies.find(
                    (c) => String(c.id) === String(record.company_id),
                  )?.name || "Ver empresa"}
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </Link>
              ) : undefined
            }
          />
        </div>
      </DetailSidePanelSection>

      {/* Contexto e Histórico */}
      <DetailSidePanelSection
        title="Observações & Histórico"
        icon={<Info className="w-4 h-4 text-purple-500" />}
        defaultOpen={true}
      >
        <div className="space-y-1">
          <InlineEditField
            label="Histórico / Background"
            field="background"
            value={record.background || ""}
            resource="contacts"
            recordId={record.id}
            previousData={record}
            type="textarea"
            onSaved={refresh}
            displayValue={
              record.background ? (
                <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
                  {record.background}
                </p>
              ) : undefined
            }
          />
        </div>
      </DetailSidePanelSection>

      {/* Tarefas e Próximos Passos */}
      <DetailSidePanelSection
        title="Tarefas & Compromissos"
        icon={<CheckSquare className="w-4 h-4 text-emerald-600" />}
        defaultOpen={true}
      >
        <div className="space-y-2">
          <ReferenceManyField
            target="contact_id"
            reference="tasks"
            sort={{ field: "due_date", order: "ASC" }}
            perPage={1000}
          >
            <TasksIterator />
          </ReferenceManyField>
          <div className="pt-2 border-t border-border/40">
            <AddTask />
          </div>
        </div>
      </DetailSidePanelSection>

      {/* Ações adicionais */}
      {link !== "edit" && (
        <div className="pt-1 flex flex-col gap-2">
          <div className="flex flex-col gap-1.5">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="pt-2 border-t border-border/40">
            <DeleteButton
              className="w-full justify-center h-8 cursor-pointer hover:bg-destructive/10! text-destructive! border border-destructive/40 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ── MultiContactField ────────────────────────────────────────────

interface MultiContactFieldProps {
  label: string;
  icon: React.ReactNode;
  items: string[];
  type?: "email" | "tel" | "text";
  onUpdate: (items: string[]) => Promise<void>;
  renderItem: (value: string, index: number) => React.ReactNode;
}

const MultiContactField = ({
  label,
  icon,
  items,
  type = "text",
  onUpdate,
  renderItem,
}: MultiContactFieldProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newValue, setNewValue] = useState("");

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(items[index]);
    setAddingNew(false);
  };

  const handleSaveEdit = async () => {
    if (!editValue.trim()) return;
    const updated = [...items];
    updated[editingIndex!] = editValue.trim();
    await onUpdate(updated);
    setEditingIndex(null);
  };

  const handleRemove = async (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    await onUpdate(updated);
  };

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    await onUpdate([...items, newValue.trim()]);
    setNewValue("");
    setAddingNew(false);
  };

  return (
    <div className="py-1">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
        {label}
      </span>
      <div className="space-y-1">
        {items.length === 0 && !addingNew && (
          <span className="text-xs text-muted-foreground italic">
            Não informado
          </span>
        )}
        {items.map((item, index) => (
          <div key={index} className="group flex items-center gap-1.5">
            {icon}
            {editingIndex === index ? (
              <div className="flex items-center gap-1 flex-1">
                <Input
                  type={type}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEdit();
                    if (e.key === "Escape") setEditingIndex(null);
                  }}
                  className="h-6 text-xs flex-1"
                  autoFocus
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={handleSaveEdit}
                >
                  <Check className="w-3.5 h-3.5 text-green-500" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => setEditingIndex(null)}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <span
                  className="flex-1 min-w-0 text-sm"
                  onClick={() => handleEdit(index)}
                  style={{ cursor: "pointer" }}
                >
                  {renderItem(item, index)}
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(index)}
                >
                  <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {addingNew ? (
          <div className="flex items-center gap-1 mt-1">
            <Input
              type={type}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") {
                  setAddingNew(false);
                  setNewValue("");
                }
              }}
              placeholder={`Novo ${label.toLowerCase()}`}
              className="h-6 text-xs flex-1"
              autoFocus
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={handleAdd}
            >
              <Check className="w-3.5 h-3.5 text-green-500" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => {
                setAddingNew(false);
                setNewValue("");
              }}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setAddingNew(true);
              setEditingIndex(null);
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors mt-0.5"
          >
            <Plus className="w-3 h-3" />
            Adicionar {label.toLowerCase()}
          </button>
        )}
      </div>
    </div>
  );
};
